import type { PortraitLayout } from '#shared/schemas/media';

/**
 * The sticker bake (add-fullbody.md section 3.2): a cutout PNG with alpha becomes a sticker with a
 * white margin, an ink outline and a grape hard shadow, all with plain 2D canvas (no ctx.filter,
 * Safari ignores it). The geometry and the alpha math are pure and tested; only `buildSticker`
 * touches canvas.
 *
 * The numbers match STICKER in shared/src/api/studio.ts at 900 px. They scale with `targetH`, so
 * the 1100 px sticker of the PNG export gets proportionally thicker lines.
 */

export const STICKER_SPEC = { h: 900, rInk: 14, rWhite: 8, shadow: [12, 9], offsets: 32 } as const;
const INK = '#000000';
const WHITE = '#ffffff';
const GRAPE = '#813f98';
const ALPHA_ON = 128;
/** Band of rows (share of the figure height) that holds the head. */
const HEAD_BAND = 0.14;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Tight box around pixels with alpha at or above `threshold`, or null for a blank image. */
export function alphaBounds(data: ArrayLike<number>, width: number, height: number, threshold = ALPHA_ON): Box | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      if ((data[row + x * 4 + 3] ?? 0) >= threshold) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** True when opaque pixels sit within 2 px of the top or bottom edge (head or feet cropped). */
export function touchesVerticalEdge(data: ArrayLike<number>, width: number, height: number): boolean {
  const rows = [0, 1, height - 2, height - 1].filter((y) => y >= 0 && y < height);
  for (const y of rows) {
    for (let x = 0; x < width; x++) if ((data[(y * width + x) * 4 + 3] ?? 0) >= ALPHA_ON) return true;
  }
  return false;
}

/** Head circle [cx, cy, r] in pixels: mean x of opaque pixels in the head band, cy at 7.5% of the figure. */
export function headCircle(data: ArrayLike<number>, width: number, bbox: Box): [number, number, number] {
  const bandEnd = Math.min(bbox.y + bbox.h, Math.ceil(bbox.y + HEAD_BAND * bbox.h));
  let sum = 0;
  let n = 0;
  for (let y = bbox.y; y < bandEnd; y++) {
    for (let x = bbox.x; x < bbox.x + bbox.w; x++) {
      if ((data[(y * width + x) * 4 + 3] ?? 0) >= ALPHA_ON) {
        sum += x;
        n++;
      }
    }
  }
  const cx = n ? sum / n : bbox.x + bbox.w / 2;
  return [cx, bbox.y + 0.075 * bbox.h, 0.075 * bbox.h];
}

export interface StickerGeometry {
  /** Canvas size of the sticker PNG. */
  width: number;
  height: number;
  /** The figure itself inside the sticker (without outline and shadow), in sticker px. */
  figure: Box;
  scale: number;
  rInk: number;
  rWhite: number;
  shadow: [number, number];
}

/**
 * Sticker canvas for a figure of `bbox` scaled to `targetH`. The margin holds the ink outline on
 * the left and top and the outline plus the shadow on the right and bottom, so nothing is clipped.
 */
export function stickerGeometry(bbox: Box, targetH: number): StickerGeometry {
  const k = targetH / STICKER_SPEC.h;
  const scale = targetH / bbox.h;
  const rInk = STICKER_SPEC.rInk * k;
  const rWhite = STICKER_SPEC.rWhite * k;
  const shadow: [number, number] = [STICKER_SPEC.shadow[0] * k, STICKER_SPEC.shadow[1] * k];
  const padL = Math.ceil(rInk) + 2;
  const padT = Math.ceil(rInk) + 2;
  const padR = Math.ceil(rInk + shadow[0]) + 2;
  const padB = Math.ceil(rInk + shadow[1]) + 2;
  const figW = Math.max(1, Math.round(bbox.w * scale));
  const figH = Math.max(1, Math.round(targetH));
  return {
    width: padL + figW + padR,
    height: padT + figH + padB,
    figure: { x: padL, y: padT, w: figW, h: figH },
    scale,
    rInk,
    rWhite,
    shadow,
  };
}

/** `n` evenly spaced offsets on a circle of radius `r` (the outline is the silhouette drawn at each). */
export function circleOffsets(r: number, n: number = STICKER_SPEC.offsets): [number, number][] {
  return Array.from({ length: n }, (_, i) => {
    const a = (Math.PI * 2 * i) / n;
    return [Math.cos(a) * r, Math.sin(a) * r] as [number, number];
  });
}

/**
 * The PortraitLayout of a baked sticker. `bbox` and `head` are normalized 0..1 of the cutout
 * (add-fullbody.md 3.2 says so; the schema comment says pixels, see docs/changes/cards-geometry.md),
 * `stickerBox` is in sticker pixels.
 */
export function stickerLayout(
  cutout: { w: number; h: number },
  bbox: Box,
  head: [number, number, number],
  touchesEdge: boolean,
  geo: StickerGeometry,
): PortraitLayout {
  const r4 = (n: number) => Math.round(n * 10000) / 10000;
  return {
    v: 1,
    bbox: [r4(bbox.x / cutout.w), r4(bbox.y / cutout.h), r4(bbox.w / cutout.w), r4(bbox.h / cutout.h)],
    head: [r4(head[0] / cutout.w), r4(head[1] / cutout.h), r4(head[2] / cutout.h)],
    touchesEdge,
    stickerBox: [geo.figure.x, geo.figure.y, geo.figure.w, geo.figure.h],
    stickerW: geo.width,
    stickerH: geo.height,
  };
}

type Canvas2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

function makeCanvas(w: number, h: number): AnyCanvas {
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  return new OffscreenCanvas(w, h);
}

function ctxOf(c: AnyCanvas): Canvas2D {
  const ctx = c.getContext('2d', { willReadFrequently: true }) as Canvas2D | null;
  if (!ctx) throw new Error('cards: no 2D canvas');
  return ctx;
}

/** A canvas holding `src` painted in one flat color (alpha kept). */
function silhouette(src: AnyCanvas, color: string): AnyCanvas {
  const c = makeCanvas(src.width, src.height);
  const ctx = ctxOf(c);
  ctx.drawImage(src as CanvasImageSource, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, c.width, c.height);
  return c;
}

async function loadBitmap(
  url: string,
): Promise<{ source: CanvasImageSource; w: number; h: number; close: () => void }> {
  if (typeof createImageBitmap === 'function' && typeof fetch === 'function') {
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (res.ok) {
        const bmp = await createImageBitmap(await res.blob());
        return { source: bmp, w: bmp.width, h: bmp.height, close: () => bmp.close() };
      }
    } catch {
      // fall through to the <img> path
    }
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.decoding = 'async';
  img.src = url;
  await img.decode();
  return { source: img, w: img.naturalWidth, h: img.naturalHeight, close: () => undefined };
}

function toBlob(c: AnyCanvas): Promise<Blob> {
  if ('convertToBlob' in c) return c.convertToBlob({ type: 'image/png' });
  return new Promise((resolve, reject) => {
    (c as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error('cards: toBlob gave nothing'))),
      'image/png',
    );
  });
}

/**
 * Bake a sticker (white margin, ink outline, baked grape shadow) from a cutout PNG at `targetH`
 * px tall. Studio calls this with 900 for the stored sticker, the PNG export with 1100.
 *
 * Steps: read the cutout alpha, find the figure box, scale the figure to `targetH`, then draw
 * the grape shadow (the ink layer offset), the ink layer (the silhouette at 32 offsets on a circle
 * of rInk), the white layer (rWhite) and the figure on top.
 */
export async function buildSticker(
  cutoutUrl: string,
  targetH: number,
): Promise<{ blob: Blob; layout: PortraitLayout }> {
  const bmp = await loadBitmap(cutoutUrl);
  try {
    const probe = makeCanvas(bmp.w, bmp.h);
    const pctx = ctxOf(probe);
    pctx.drawImage(bmp.source, 0, 0);
    const { data } = pctx.getImageData(0, 0, bmp.w, bmp.h);
    const bbox = alphaBounds(data, bmp.w, bmp.h);
    if (!bbox) throw new Error('cards: the cutout is empty');
    const head = headCircle(data, bmp.w, bbox);
    const edge = touchesVerticalEdge(data, bmp.w, bmp.h);
    const geo = stickerGeometry(bbox, targetH);

    // The figure alone, scaled, on its own canvas: the base of every layer.
    const fig = makeCanvas(geo.figure.w, geo.figure.h);
    const fctx = ctxOf(fig);
    fctx.imageSmoothingQuality = 'high';
    fctx.drawImage(bmp.source, bbox.x, bbox.y, bbox.w, bbox.h, 0, 0, geo.figure.w, geo.figure.h);

    const ink = silhouette(fig, INK);
    const white = silhouette(fig, WHITE);
    const grape = silhouette(fig, GRAPE);

    const out = makeCanvas(geo.width, geo.height);
    const ctx = ctxOf(out);
    const { x: fx, y: fy } = geo.figure;
    const [sx, sy] = geo.shadow;
    for (const [dx, dy] of circleOffsets(geo.rInk))
      ctx.drawImage(grape as CanvasImageSource, fx + dx + sx, fy + dy + sy);
    for (const [dx, dy] of circleOffsets(geo.rInk)) ctx.drawImage(ink as CanvasImageSource, fx + dx, fy + dy);
    for (const [dx, dy] of circleOffsets(geo.rWhite)) ctx.drawImage(white as CanvasImageSource, fx + dx, fy + dy);
    ctx.drawImage(fig as CanvasImageSource, fx, fy);

    return { blob: await toBlob(out), layout: stickerLayout({ w: bmp.w, h: bmp.h }, bbox, head, edge, geo) };
  } finally {
    bmp.close();
  }
}

/**
 * Layout of a finished sticker PNG that arrives without one (older rows): the alpha box of the
 * image, which includes the outline. Good enough to place it; the baked path stays exact.
 */
export async function measureSticker(
  url: string,
): Promise<{ stickerBox: [number, number, number, number]; stickerW: number; stickerH: number }> {
  const bmp = await loadBitmap(url);
  try {
    const c = makeCanvas(bmp.w, bmp.h);
    const ctx = ctxOf(c);
    ctx.drawImage(bmp.source, 0, 0);
    const box = alphaBounds(ctx.getImageData(0, 0, bmp.w, bmp.h).data, bmp.w, bmp.h) ?? {
      x: 0,
      y: 0,
      w: bmp.w,
      h: bmp.h,
    };
    return { stickerBox: [box.x, box.y, box.w, box.h], stickerW: bmp.w, stickerH: bmp.h };
  } finally {
    bmp.close();
  }
}
